import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, Menu, MenuItem, ListItemIcon, ListItemText, IconButton, Tooltip, Divider, CircularProgress, Button } from '@mui/material';
import { styled, type Theme, useTheme, alpha } from '@mui/material/styles';
import { Category } from '../../types/Category';
import { Content } from '../../types/Content';
import { cutPendingSx } from '../../utils/DocumentUtils';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import { AutoFixHighDirectionIcon } from '../icons/AiSparklesIcon';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { useHelpContent } from '../../utils/HelpUtils';
import { contentHasMedia, getContentDisplayIndex, getContentMediaIndicatorTooltip, isContentCategoryEmptyForDisplay } from '../../utils/CategoryUtils';
import { getContentPanePreviewText } from '../../utils/contentRichPreviewHtml';
import {
  branchChildrenAreLoaded,
  categoryContentsAreLoaded,
} from '../../utils/categoryLoadState';

function findCategoryById(categories: Category[], targetId: string): Category | null {
  const category = categories.find((cat) => cat.id === targetId);
  if (category) return category;
  for (const parent of categories) {
    if (parent.children) {
      const found = findCategoryById(parent.children, targetId);
      if (found) return found;
    }
  }
  return null;
}

function findContentById(cats: Category[], id: string): Content | null {
  if (!cats || !Array.isArray(cats) || !id) return null;
  for (const cat of cats) {
    if (cat.contentCategory) {
      if (cat.contents) {
        const content = cat.contents.find((c) => c.id === id);
        if (content) return content;
      }
    } else if (cat.children) {
      const found = findContentById(cat.children, id);
      if (found) return found;
    }
  }
  return null;
}

interface CategoryContentProps {
  categories: Category[];
  selectedCategory: Category | null;
  onCategoryMultiSelect: (category: Category, event: React.MouseEvent) => void;
  /** Checkbox in the child-category table: toggle without Ctrl/Cmd. */
  onCategoryCheckboxToggle?: (category: Category) => void;
  onContentMultiSelect: (content: Content, event: React.MouseEvent) => void;
  /** Checkbox in the content table: toggle selection; shows editor when exactly one row is selected. */
  onContentCheckboxToggle?: (content: Content) => void;
  /** Row currently shown in the content editor (may differ from sole selection when multi-selected). */
  viewedContentId?: string | null;
  selectedCategories: Category[];
  selectedContents: Content[];
  onSelectAllCategories: (checked: boolean) => void;
  onSelectAllContents: (checked: boolean, contentsScope?: Content[]) => void;
  onInvertSelectionCategories?: () => void;
  onInvertSelectionContents?: (contentsScope?: Content[]) => void;
  onEditContent?: () => void;
  /** Adjust text of a single (non-rich) content row by borrowing from its neighbours. */
  onAdjustTextContent?: () => void;
  onGroupCategories?: () => void;
  onOrganizeCategories?: () => void;
  onCollectBranchContent?: () => void;
  onMergeCategories?: () => void;
  onDeleteSelected?: () => void;
  /** Right-click on a category row: select that row when nothing is selected yet. */
  onCategoryContextMenu?: (category: Category) => void;
  /** Double-click a child category row: select it in the tree and show its contents. */
  onCategoryNavigate?: (category: Category) => void;
  /** Navigate to the parent of the category currently shown in the pane. */
  onNavigateToParent?: () => void;
  /** Right-click on a content row: select that row when nothing is selected yet. */
  onContentContextMenu?: (content: Content) => void;
  /** Whether Delete Category is allowed for the current pane selection (no content rows selected). */
  canDeletePaneCategories?: () => boolean;
  onRenameCategory?: () => void;
  /** Single selected content-category row in the pane (no content rows selected). */
  canRenamePaneCategory?: () => boolean;
  onMoveSelected?: (direction: 'up' | 'down') => void;
  onCutSelected?: () => void;
  onCopySelected?: () => void;
  canEditContent?: boolean;
  /** Whether Adjust text is allowed (single non-rich content row selected with a neighbour). */
  canAdjustTextContent?: boolean;
  canGroupCategories?: boolean;
  canOrganizeCategories?: () => boolean;
  canCollectBranchContent?: () => boolean;
  canMergeCategories?: boolean;
  canDeleteSelected?: boolean;
  canMoveSelected?: (direction: 'up' | 'down') => boolean;
  canCutSelected?: boolean;
  canCopySelected?: boolean;
  onDropCategories?: (categories: Category[], targetCategory: Category, position: 'before' | 'after' | 'inside') => void;
  onDropCategoriesIntoContent?: (
    sourceCategories: Category[],
    targetContentCategory: Category,
    copy: boolean,
  ) => void;
  onDropCategoriesIntoBranch?: (
    sourceCategories: Category[],
    targetBranch: Category,
    copy: boolean,
  ) => void;
  onDropContents?: (contents: Content[], targetContent: Content, position: 'before' | 'after' | 'inside') => void;
  matchingContentIds?: string[];
  /** When non-null, only contents whose `subcase` is in this list are shown in the content table. */
  visibleSubcaseFilter?: string[] | null;
  /** When true, content cannot be reordered by dropping on other content rows; dragging to a category in the tree still works. */
  contentReorderDisabled?: boolean;
  /** When true, show each content row's 1-based index (from id suffix) in the table. */
  showContentIndex?: boolean;
  /** Loading state while the pane fetches category data for navigation. */
  paneLoading?: boolean;
  /** Content row ids cut to the clipboard (pending paste). */
  cutContentIds?: ReadonlySet<string>;
  /** Category ids cut to the clipboard (pending paste). */
  cutCategoryIds?: ReadonlySet<string>;
}

const ContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2, 2, 2),
  height: '100%',
  minHeight: 0,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const tableContainerScrollSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
} as const;

const tableScrollSurfaceSx = (theme: Theme) => ({
  ...tableContainerScrollSx,
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.action.hover,
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.divider,
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.palette.text.disabled,
    },
  },
});

const stickyTableHeadSx = (theme: Theme) => ({
  '& .MuiTableCell-root': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
});

const StyledTable = styled(Table)({
  fontSize: 'var(--app-font-size, 14px)',
  '& .MuiTableCell-root': {
    padding: '4px 8px',
    fontSize: 'var(--app-font-size, 14px)',
  },
  '& .MuiTableRow-root': {
    height: '32px',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    cursor: 'grab',
  },
  '& .MuiTableBody-root .MuiTableRow-root:active': {
    cursor: 'grabbing',
  },
});

const ResizeHandle = styled('div')({
  width: '4px',
  height: '100%',
  cursor: 'col-resize',
  position: 'absolute',
  right: 0,
  top: 0,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: '#1976d2',
  },
});

const ResizableTableCell = styled(TableCell)({
  padding: '4px 8px',
  '&:hover .resize-handle': {
    backgroundColor: '#1976d2',
  },
});

const isContent = (item: Category | Content): item is Content => {
  return 'subcase' in item && 'text' in item;
};

const invertSelectionButtonSx = {
  minWidth: 0,
  px: 0.5,
  py: 0,
  fontSize: '0.7rem',
  lineHeight: 1.2,
  textTransform: 'none',
  flexShrink: 0,
} as const;

/** Compact control for invert selection (text label, not an icon). */
export const InvertSelectionButton = ({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) => (
  <Tooltip title="Select all unselected rows and clear the current selection">
    <span>
      <Button
        size="small"
        variant="text"
        disabled={disabled}
        onClick={onClick}
        aria-label="Invert selection"
        sx={invertSelectionButtonSx}
      >
        Invert
      </Button>
    </span>
  </Tooltip>
);

const CategoryContent = forwardRef<HTMLDivElement, CategoryContentProps>(({
  categories,
  selectedCategory,
  onCategoryMultiSelect,
  onCategoryCheckboxToggle,
  onContentMultiSelect,
  onContentCheckboxToggle,
  viewedContentId = null,
  selectedCategories,
  selectedContents,
  onSelectAllCategories,
  onSelectAllContents,
  onInvertSelectionCategories,
  onInvertSelectionContents,
  onEditContent,
  onAdjustTextContent,
  onGroupCategories,
  onOrganizeCategories,
  onCollectBranchContent,
  onMergeCategories,
  onDeleteSelected,
  onCategoryContextMenu,
  onCategoryNavigate,
  onNavigateToParent,
  onContentContextMenu,
  canDeletePaneCategories,
  onRenameCategory,
  canRenamePaneCategory,
  onMoveSelected,
  onCutSelected,
  onCopySelected,
  canEditContent = false,
  canAdjustTextContent = false,
  canGroupCategories = false,
  canOrganizeCategories = () => false,
  canCollectBranchContent = () => false,
  canMergeCategories = false,
  canDeleteSelected = false,
  canMoveSelected = () => false,
  canCutSelected = false,
  canCopySelected = false,
  onDropCategories,
  onDropCategoriesIntoContent,
  onDropCategoriesIntoBranch,
  onDropContents,
  matchingContentIds = [],
  visibleSubcaseFilter = null,
  contentReorderDisabled = false,
  showContentIndex = false,
  paneLoading = false,
  cutContentIds = new Set(),
  cutCategoryIds = new Set(),
}, ref) => {
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({
    checkbox: 5,
    type: 5,
    index: 44,
    name: 100,
    description: 200,
    subcase: 100,
    content: 400,
  });

  const resizeRef = useRef<{
    columnId: string | null;
    startX: number;
    startWidth: number;
  }>({
    columnId: null,
    startX: 0,
    startWidth: 0,
  });

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    type: 'category' | 'content';
    item: Category | Content;
  } | null>(null);

  const contentContainerRef = useRef<HTMLDivElement>(null);
  const lastCategoryClickRef = useRef<{ categoryId: string; at: number } | null>(null);
  const { getMenuTooltipTextForState } = useHelpContent();

  // Expose the ref to the parent component
  useImperativeHandle(ref, () => contentContainerRef.current!);

  const handleResizeStart = (e: React.MouseEvent, columnId: string) => {
    resizeRef.current = {
      columnId,
      startX: e.clientX,
      startWidth: columnWidths[columnId],
    };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (resizeRef.current.columnId) {
      const { columnId, startX, startWidth } = resizeRef.current;
      const newWidth = startWidth + (e.clientX - startX);
      setColumnWidths(prev => ({
        ...prev,
        [columnId]: Math.max(48, newWidth),
      }));
    }
  };

  const handleResizeEnd = () => {
    resizeRef.current.columnId = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  const handleContextMenu = (event: React.MouseEvent, type: 'category' | 'content', item: Category | Content) => {
    event.preventDefault();
    event.stopPropagation();

    if (selectedCategories.length === 0 && selectedContents.length === 0) {
      if (type === 'category' && !isContent(item)) {
        onCategoryContextMenu?.(item);
      } else if (type === 'content' && isContent(item)) {
        onContentContextMenu?.(item);
      }
    }

    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      type,
      item,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const renderPaneNavigation = () => {
    const canGoBack = Boolean(selectedCategory?.parentId && onNavigateToParent);
    const drillTarget =
      selectedCategories.length === 1 && onCategoryNavigate
        ? selectedCategories[0]
        : null;
    const canDrillDown = Boolean(
      drillTarget &&
        selectedCategory?.children?.some((child) => child.id === drillTarget.id),
    );

    if (!canGoBack && !canDrillDown) {
      return null;
    }

    const parent = selectedCategory?.parentId
      ? findCategoryById(categories, selectedCategory.parentId)
      : null;
    const parentLabel = parent?.name ?? 'parent';

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          flexShrink: 0,
          minHeight: 0,
          height: 24,
        }}
      >
        {canGoBack && (
          <Tooltip title={`Back to ${parentLabel}`}>
            <IconButton
              size="small"
              onClick={onNavigateToParent}
              aria-label={`Back to ${parentLabel}`}
              disabled={paneLoading}
              sx={{ p: 0.25, width: 24, height: 24 }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        {canDrillDown && drillTarget && (
          <Tooltip title={`Open ${drillTarget.name}`}>
            <IconButton
              size="small"
              onClick={() => onCategoryNavigate!(drillTarget)}
              aria-label={`Open ${drillTarget.name}`}
              disabled={paneLoading}
              sx={{ p: 0.25, width: 24, height: 24 }}
            >
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  const wrapPaneBody = (body: React.ReactNode) => (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {body}
      {paneLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.65),
            zIndex: 3,
          }}
        >
          <CircularProgress size={28} aria-label="Loading category" />
        </Box>
      )}
    </Box>
  );

  if (!selectedCategory) {
    return (
      <ContentContainer ref={contentContainerRef}>
        <Typography variant="h6">Select a category to view its contents</Typography>
      </ContentContainer>
    );
  }

  const renderCategoryDataLoadGate = (
    kind: 'subcategories' | 'content rows',
    loadState: Category['childrenLoadState'] | Category['contentsLoadState'],
    loadError?: string,
  ) => {
    const state = loadState ?? 'loaded';
    if (state === 'loaded') {
      return null;
    }
    const label = kind === 'subcategories' ? 'subcategories' : 'content rows';
    let message: string;
    if (state === 'loading') {
      message = `Loading ${label}…`;
    } else if (state === 'error') {
      message = loadError ?? `Failed to load ${label}`;
    } else {
      message = `${label.charAt(0).toUpperCase()}${label.slice(1)} have not been loaded yet`;
    }
    return (
      <ContentContainer ref={contentContainerRef}>
        {wrapPaneBody(
          <>
            {renderPaneNavigation()}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                p: 3,
              }}
            >
              {state === 'loading' && <CircularProgress size={28} />}
              <Typography
                variant="body1"
                color={state === 'error' ? 'error' : 'text.secondary'}
                align="center"
              >
                {message}
              </Typography>
            </Box>
          </>,
        )}
      </ContentContainer>
    );
  };

  if (selectedCategory.contentCategory && !categoryContentsAreLoaded(selectedCategory)) {
    return renderCategoryDataLoadGate(
      'content rows',
      selectedCategory.contentsLoadState,
      selectedCategory.loadError,
    );
  }

  if (!selectedCategory.contentCategory && !branchChildrenAreLoaded(selectedCategory)) {
    return renderCategoryDataLoadGate(
      'subcategories',
      selectedCategory.childrenLoadState,
      selectedCategory.loadError,
    );
  }

  const handleCategoryClick = (event: React.MouseEvent, category: Category) => {
    const now = Date.now();
    const last = lastCategoryClickRef.current;
    if (
      onCategoryNavigate &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      (event.detail >= 2 ||
        (last?.categoryId === category.id && now - last.at < 450))
    ) {
      lastCategoryClickRef.current = null;
      event.preventDefault();
      event.stopPropagation();
      onCategoryNavigate(category);
      return;
    }
    lastCategoryClickRef.current = { categoryId: category.id, at: now };
    onCategoryMultiSelect(category, event);
  };

  const handleContentClick = (event: React.MouseEvent, content: Content) => {
    onContentMultiSelect(content, event);
  };

  const isCategorySelected = (category: Category) => {
    return selectedCategories.some(c => c.id === category.id);
  };

  const isContentSelected = (content: Content) => {
    return selectedContents.some(c => c.id === content.id);
  };

  const handleSelectAllCategories = (event: React.ChangeEvent<HTMLInputElement>) => {
     onSelectAllCategories(event.target.checked);
   };

type DraggableContentRowProps = {
  content: Content;
  onClick: (event: React.MouseEvent, content: any) => void;
  onCheckboxToggle: (content: Content) => void;
  onContextMenu: (event: React.MouseEvent, content: any) => void;
  isSelected: boolean;
  isViewed: boolean;
  selectedContents: Content[];
  matchingContentIds?: string[];
  reorderDisabled?: boolean;
  showContentIndex?: boolean;
  isCutPending?: boolean;
};

const DraggableContentRow: React.FC<DraggableContentRowProps> = ({
  content,
  onClick,
  onCheckboxToggle,
  onContextMenu,
  isSelected,
  isViewed,
  selectedContents,
  matchingContentIds = [],
  reorderDisabled = false,
  showContentIndex = false,
  isCutPending = false,
}) => {
  const theme = useTheme();
  type ContentsDragItem = {
    type: 'CONTENT_ROW';
    contents: Content[];
  };

  const ref = useRef<HTMLDivElement | null>(null);

  const [dropTarget, setDropTarget] = useState<{
    contentId: string;
    position: 'before' | 'after' | 'inside';
  } | null>(null);

  const [{ isDragging }, drag] = useDrag(() => ({
      type: 'CONTENT_ROW',
      // Always allow dragging selected rows so content can be dropped on categories in the tree;
      // reorderDisabled only turns off useDrop between content rows below.
      canDrag: () =>
        selectedContents.length > 0 &&
        selectedContents.some(c => c.id === content.id),
      item: () => {
          return {type: 'CONTENT_ROW', contents: selectedContents};
        },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }), [selectedContents]);

  const [{ isOver, itemType }, drop] = useDrop<ContentsDragItem, void, { isOver: boolean; itemType: string | null }>({
    accept: 'CONTENT_ROW',
    canDrop: () => !reorderDisabled,
    drop: (item: { contents: Content[] }, monitor) => {
        if (reorderDisabled || !dropTarget || item.contents.some(c => c.id === content.id)) return;

        const targetContent = findContentById(categories, dropTarget.contentId);
        if ( targetContent)
            onDropContents?.(item.contents, targetContent, dropTarget.position);

    },
     hover(item: ContentsDragItem, monitor: DropTargetMonitor<ContentsDragItem, void>) {
       if (reorderDisabled) {
         setDropTarget(null);
         return;
       }
       if (!ref.current) return;

         const isOver = monitor.isOver({ shallow: true });
         if (!isOver) {
           setDropTarget(null);
           return;
         }

       const clientOffset = monitor.getClientOffset();
       const boundingRect = ref.current?.getBoundingClientRect();
       if (!clientOffset || !boundingRect) return;

       const y = clientOffset.y - boundingRect.top;
       const height = boundingRect.height;

       let position: 'before' | 'after' | 'inside' = 'inside';
       if (y < height / 3) position = 'before';
       else if (y > (2 * height) / 3) position = 'after';

       setDropTarget({ contentId: content.id, position });
     },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      itemType: monitor.getItemType() as string | null,
    }),
  });

  useEffect(() => {
       if (!isOver) {
         setDropTarget((prev) => {
           // Only clear if this item owns the current dropTarget
           if (prev?.contentId === content.id) {
             return null;
           }
           return prev;
         });
       }
     }, [isOver, content.id]);

  return (
    <TableRow
        ref={(node: HTMLDivElement | null) => {
             ref.current = node; // This assigns the ref
             if (node) drag(drop(node)); // Attach DnD
         }}
       data-content-id={content.id}
       sx={{
            opacity: isDragging ? 0.5 : undefined,
            alignItems: 'center',
            width: '100%',
            cursor: isSelected ? 'grab' : reorderDisabled ? 'default' : 'move',
            '&:active': { cursor: isSelected ? 'grabbing' : reorderDisabled ? 'default' : 'grabbing' },
            padding: '4px 8px',
            borderRadius: '4px',
            '&:hover': { backgroundColor: theme.palette.action.hover },
            borderTop:
              dropTarget?.contentId === content.id && dropTarget?.position === 'before'
                ? `2px solid ${theme.palette.primary.light}`
                : undefined,
            borderBottom:
              dropTarget?.contentId === content.id && dropTarget?.position === 'after'
                ? `2px solid ${theme.palette.primary.light}`
                : undefined,
            backgroundColor:
              dropTarget?.contentId === content.id && dropTarget?.position === 'inside'
                ? alpha(theme.palette.primary.main, 0.1)
                : matchingContentIds.includes(content.id)
                ? 'rgba(255, 255, 0, 0.4)' // Yellow highlight for search matches
                : isViewed && !isSelected
                  ? alpha(theme.palette.primary.main, 0.08)
                  : undefined,
            border:
              matchingContentIds.includes(content.id)
                ? '2px solid #ffd700' // Gold border for search matches
                : undefined,
            borderLeft:
              isViewed && !matchingContentIds.includes(content.id)
                ? `3px solid ${theme.palette.primary.main}`
                : undefined,
            ...cutPendingSx(isCutPending && !isDragging),
          }}
      key={content.id}
      hover
      onClick={(e) => onClick(e, content)}
      selected={isSelected}
      onContextMenu={(e) => onContextMenu(e, content)}
    >
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          checked={isSelected}
          onClick={(event) => {
            event.stopPropagation();
            onCheckboxToggle(content);
          }}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <StickyNote2OutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-hidden />
          {contentHasMedia(content) ? (
            <Tooltip title={getContentMediaIndicatorTooltip(content)} placement="top">
              <PermMediaOutlinedIcon
                sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }}
                aria-label="Has media"
              />
            </Tooltip>
          ) : null}
        </Box>
      </TableCell>
      {showContentIndex && (
        <TableCell
          sx={{
            color: 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
            pr: 1,
          }}
        >
          {getContentDisplayIndex(content.id) ?? '—'}
        </TableCell>
      )}
      <TableCell>{content.subcase}</TableCell>
      <TableCell>
        <Typography
          component="span"
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: content.text.trim() ? 'text.primary' : 'text.secondary',
            fontStyle: content.text.trim() ? 'normal' : 'italic',
          }}
        >
          {getContentPanePreviewText(content.text, content.richTextHtml).trim()
            ? (() => {
                const flat = getContentPanePreviewText(content.text, content.richTextHtml);
                return flat.length > 100 ? `${flat.substring(0, 100)}...` : flat;
              })()
            : contentHasMedia(content)
              ? '(media only)'
              : ''}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

type DraggableCategoryRowProps = {
    category: Category;
    onClick: (event: React.MouseEvent, content: any) => void;
    onContextMenu: (event: React.MouseEvent, content: any) => void;
    onCheckboxToggle?: (category: Category) => void;
    isSelected: boolean;
    selectedCategories: Category[];
    isCutPending?: boolean;
  };

  const DraggableCategoryRow: React.FC<DraggableCategoryRowProps> = ({
    category,
    onClick,
    onContextMenu,
    onCheckboxToggle,
    isSelected,
    isCutPending = false,
  }) => {
    const theme = useTheme();
    const resolvedCategory = findCategoryById(categories, category.id) ?? category;
    const hasChildren = (resolvedCategory.children?.length ?? 0) > 0;
    const isEmptyContentCategory = isContentCategoryEmptyForDisplay(
      resolvedCategory,
      visibleSubcaseFilter,
    );

    const renderCategoryTypeIcon = () => {
      if (resolvedCategory.contentCategory) {
        return isEmptyContentCategory ? (
          <DescriptionOutlinedIcon sx={{ color: 'text.secondary', mr: 1 }} />
        ) : (
          <DescriptionIcon sx={{ color: 'text.secondary', mr: 1 }} />
        );
      }
      if (hasChildren) {
        return <FolderIcon sx={{ color: 'text.secondary', mr: 1 }} />;
      }
      return <DescriptionIcon sx={{ color: 'text.secondary', mr: 1 }} />;
    };

   const ref = useRef<HTMLDivElement | null>(null);

  const [dropTarget, setDropTarget] = useState<{
    categoryId: string;
    position: 'before' | 'after' | 'inside';
  } | null>(null);


   type CategoriesDragItem = {
       type: 'CATEGORY_ROW';
       categories: Category[];
     };

    const [{ isDragging }, drag] = useDrag(() => ({
          type: 'CATEGORY_ROW',
          canDrag: () => {return selectedCategories.length > 0 && selectedCategories.some(c => c.id === category.id)},
          item: () => {
              return {type: 'CATEGORY_ROW', categories: selectedCategories};
            },
          collect: (monitor) => ({
            isDragging: monitor.isDragging(),
          }),
        }), [selectedCategories]);

      const [{ isOver, itemType }, drop] = useDrop<CategoriesDragItem, void, { isOver: boolean; itemType: string | null }>({
        accept: 'CATEGORY_ROW',
        drop: (item: { categories: Category[] }, monitor) => {
            if (!dropTarget || item.categories.some(c => c.id === category.id)) return;

            const targetCategory = findCategoryById(categories, dropTarget.categoryId);
            if (!targetCategory) return;
            if (
              dropTarget.position === 'inside' &&
              targetCategory.contentCategory &&
              item.categories.length >= 2
            ) {
              onDropCategoriesIntoContent?.(item.categories, targetCategory, false);
              return;
            }
            if (
              dropTarget.position === 'inside' &&
              !targetCategory.contentCategory &&
              item.categories.length >= 2
            ) {
              onDropCategoriesIntoBranch?.(item.categories, targetCategory, false);
              return;
            }
            onDropCategories?.(item.categories, targetCategory, dropTarget.position);

        },
         hover(item: CategoriesDragItem, monitor: DropTargetMonitor<CategoriesDragItem, void>) {
           if (!ref.current) return;

             const isOver = monitor.isOver({ shallow: true });
             if (!isOver) {
               setDropTarget(null);
               return;
             }

           const clientOffset = monitor.getClientOffset();
           const boundingRect = ref.current?.getBoundingClientRect();
           if (!clientOffset || !boundingRect) return;

           const y = clientOffset.y - boundingRect.top;
           const height = boundingRect.height;

           let position: 'before' | 'after' | 'inside' = 'inside';
           if (y < height / 3) position = 'before';
           else if (y > (2 * height) / 3) position = 'after';

           setDropTarget({ categoryId: category.id, position });
         },
        collect: (monitor) => ({
          isOver: monitor.isOver(),
          itemType: monitor.getItemType() as string | null,
        }),
      });

      useEffect(() => {
           if (!isOver) {
             setDropTarget((prev) => {
               // Only clear if this item owns the current dropTarget
               if (prev?.categoryId === category.id) {
                 return null;
               }
               return prev;
             });
           }
         }, [isOver, category.id]);


    return (
     <TableRow
       ref={(node: HTMLDivElement | null) => {
            ref.current = node; // This assigns the ref
            if (node) drag(drop(node)); // Attach DnD
        }}
          sx={{
               opacity: isDragging ? 0.5 : undefined,
               alignItems: 'center',
               width: '100%',
               cursor:  isSelected ? 'grab' : 'move',
               '&:active': { cursor: 'grabbing' },
               padding: '4px 8px',
               borderRadius: '4px',
               '&:hover': { backgroundColor: theme.palette.action.hover },
               borderTop:
                 dropTarget?.categoryId === category.id && dropTarget?.position === 'before'
                   ? `2px solid ${theme.palette.primary.light}`
                   : undefined,
               borderBottom:
                 dropTarget?.categoryId === category.id && dropTarget?.position === 'after'
                   ? `2px solid ${theme.palette.primary.light}`
                   : undefined,
               backgroundColor:
                 dropTarget?.categoryId === category.id && dropTarget?.position === 'inside'
                   ? alpha(theme.palette.primary.main, 0.1)
                   : undefined,
               ...cutPendingSx(isCutPending && !isDragging),
             }}
         key={category.id}
         hover
         onClick={(e) => onClick(e, category)}
         selected={isSelected}
         onContextMenu={(e) => onContextMenu(e, category)}
       >
       <TableCell padding="checkbox">
         <Checkbox
           checked={isSelected}
           onClick={(event) => {
             event.stopPropagation();
             if (event.shiftKey) {
               handleCategoryClick(event, category);
             } else if (onCheckboxToggle) {
               onCheckboxToggle(category);
             } else {
               handleCategoryClick(event, category);
             }
           }}
               />
             </TableCell>
       <TableCell>{renderCategoryTypeIcon()}</TableCell>
       <TableCell
         sx={{
           ...(onCategoryNavigate ? { cursor: 'pointer' } : {}),
           ...(isCutPending ? { fontStyle: 'italic', color: 'text.secondary' } : {}),
         }}
       >
         {category.name}
       </TableCell>
       <TableCell>{category.description}</TableCell>
     </TableRow>
   );
  }

  if (selectedCategory.contentCategory) {
    const contentsInView =
      visibleSubcaseFilter != null && selectedCategory.contents
        ? selectedCategory.contents.filter(c => visibleSubcaseFilter.includes(c.subcase))
        : selectedCategory.contents ?? [];

    const visibleSelectedCount = selectedContents.filter(c =>
      contentsInView.some(v => v.id === c.id)
    ).length;

    const handleSelectAllContentsInView = (event: React.ChangeEvent<HTMLInputElement>) => {
      onSelectAllContents(event.target.checked, contentsInView);
    };

    const canInvertContents =
      contentsInView.length > 0 && Boolean(onInvertSelectionContents);

  return (
      <ContentContainer ref={contentContainerRef}>
        {wrapPaneBody(
          <>
        {renderPaneNavigation()}
        <TableContainer component={Paper} sx={tableScrollSurfaceSx}>
          <StyledTable size="small">
            <TableHead sx={stickyTableHeadSx}>
              <TableRow>
              <ResizableTableCell style={{ width: columnWidths.checkbox }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      indeterminate={
                        visibleSelectedCount > 0 &&
                        contentsInView.length > 0 &&
                        visibleSelectedCount < contentsInView.length
                      }
                      checked={
                        contentsInView.length > 0 &&
                        visibleSelectedCount === contentsInView.length
                      }
                      onChange={handleSelectAllContentsInView}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {visibleSelectedCount} / {contentsInView.length}
                    </Typography>
                    {onInvertSelectionContents && (
                      <InvertSelectionButton
                        disabled={!canInvertContents}
                        onClick={() => onInvertSelectionContents(contentsInView)}
                      />
                    )}
                  </Box>
                </ResizableTableCell>
              <TableCell style={{ width: columnWidths.type }}></TableCell>
              {showContentIndex && (
                <ResizableTableCell style={{ width: columnWidths.index }}>
                  No.
                  <ResizeHandle
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'index')}
                  />
                </ResizableTableCell>
              )}
              <ResizableTableCell style={{ width: columnWidths.subcase }}>
                Subcase
                <ResizeHandle
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, 'subcase')}
                />
              </ResizableTableCell>
              <ResizableTableCell style={{ width: columnWidths.content }}>
                Content
                <ResizeHandle
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, 'content')}
                />
              </ResizableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {contentsInView.map((content) => (
               <DraggableContentRow
                  key={content.id}
                  content={content}
                  onClick={(event) => handleContentClick(event, content)}
                  onCheckboxToggle={(row) =>
                    onContentCheckboxToggle
                      ? onContentCheckboxToggle(row)
                      : handleContentClick({ ctrlKey: true } as React.MouseEvent, row)
                  }
                  onContextMenu={(event) => handleContextMenu(event, 'content', content)}
                  isSelected={isContentSelected(content)}
                  isViewed={viewedContentId === content.id}
                  selectedContents={selectedContents}
                  matchingContentIds={matchingContentIds}
                  reorderDisabled={contentReorderDisabled}
                  showContentIndex={showContentIndex}
                  isCutPending={cutContentIds.has(content.id)}
                />
            ))}
          </TableBody>
        </StyledTable>
        <Menu
          open={contextMenu?.type === 'content' ? true : false}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          {onInvertSelectionContents && (
            <MenuItem
              onClick={() => {
                onInvertSelectionContents(contentsInView);
                handleCloseContextMenu();
              }}
              disabled={contentsInView.length === 0}
            >
              <ListItemText>Invert selection</ListItemText>
            </MenuItem>
          )}
          {onInvertSelectionContents && <Divider />}
          <Tooltip title={getMenuTooltipTextForState('edit-content', !!(selectedContents.length > 0 && canEditContent))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onEditContent?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canEditContent}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit Content</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          {onAdjustTextContent && (
            <Tooltip
              title={getMenuTooltipTextForState('adjust-text-content', !!canAdjustTextContent)}
              placement="right"
            >
              <span>
                <MenuItem
                  onClick={() => {
                    onAdjustTextContent();
                    handleCloseContextMenu();
                  }}
                  disabled={!canAdjustTextContent}
                >
                  <ListItemIcon>
                    <NotesIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Adjust text…</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
          <Tooltip title={getMenuTooltipTextForState('delete-content', !!(selectedContents.length > 0 && canDeleteSelected))} placement="right">
          <span>
            <MenuItem
              onClick={() => {
                onDeleteSelected?.();
                handleCloseContextMenu();
              }}
              disabled={selectedContents.length === 0 || !canDeleteSelected}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete Content</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('move-content-up', !!(selectedContents.length > 0 && canMoveSelected('up')))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onMoveSelected?.('up');
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canMoveSelected('up')}
              >
                <ListItemIcon>
                  <ArrowUpwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Up</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('move-content-down', !!(selectedContents.length > 0 && canMoveSelected('down')))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onMoveSelected?.('down');
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canMoveSelected('down')}
              >
                <ListItemIcon>
                  <ArrowDownwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Down</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('cut-content', !!(selectedContents.length > 0 && canCutSelected))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onCutSelected?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canCutSelected}
              >
                <ListItemIcon>
                  <ContentCutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cut</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('copy-content', !!(selectedContents.length > 0 && canCopySelected))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onCopySelected?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedContents.length === 0 || !canCopySelected}
              >
                <ListItemIcon>
                  <ContentCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
        </TableContainer>
          </>,
        )}
      </ContentContainer>
    );
  }

  const childCount = selectedCategory.children?.length ?? 0;
  const canInvertCategories = childCount > 0 && Boolean(onInvertSelectionCategories);

  return (
    <ContentContainer ref={contentContainerRef}>
      {wrapPaneBody(
        <>
      {renderPaneNavigation()}
      <TableContainer component={Paper} sx={tableScrollSurfaceSx}>
                    <StyledTable className="table-fixed w-full border" size="small">
          <TableHead sx={stickyTableHeadSx}>
            <TableRow>
              <TableCell className="w-12" style={{ width: columnWidths.checkbox }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    size="small"
                    indeterminate={
                      selectedCategories.length > 0 &&
                      selectedCategories.length < childCount
                    }
                    checked={
                      childCount > 0 &&
                      selectedCategories.length === childCount
                    }
                    onChange={handleSelectAllCategories}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {selectedCategories.length} / {childCount}
                  </Typography>
                  {onInvertSelectionCategories && (
                    <InvertSelectionButton
                      disabled={!canInvertCategories}
                      onClick={onInvertSelectionCategories}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell style={{ width: columnWidths.type }}>Type</TableCell>
              <ResizableTableCell style={{ width: columnWidths.name }}>
                Name
                <ResizeHandle
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, 'name')}
                />
              </ResizableTableCell>
              <ResizableTableCell style={{ width: columnWidths.description }}>
                Description
                <ResizeHandle
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, 'description')}
                />
              </ResizableTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedCategory.children?.map((category) => (
              <DraggableCategoryRow
                key={category.id}
                category={category}
                onClick={(event) => handleCategoryClick(event, category)}
                onContextMenu={(event) => handleContextMenu(event, 'category', category)}
                onCheckboxToggle={onCategoryCheckboxToggle}
                isSelected={isCategorySelected(category)}
                selectedCategories={selectedCategories}
                isCutPending={cutCategoryIds.has(category.id)}
              />
            ))}
          </TableBody>
        </StyledTable>
        <Menu
          open={contextMenu?.type === 'category' ? true : false}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          {onInvertSelectionCategories && (
            <MenuItem
              onClick={() => {
                onInvertSelectionCategories();
                handleCloseContextMenu();
              }}
              disabled={childCount === 0}
            >
              <ListItemText>Invert selection</ListItemText>
            </MenuItem>
          )}
          {onInvertSelectionCategories && <Divider />}
          <Tooltip title={getMenuTooltipTextForState('group-categories', !!(selectedCategories.length > 0 && canGroupCategories))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onGroupCategories?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canGroupCategories}
              >
                <ListItemIcon>
                  <CallMergeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Group categories</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('structure-categories', canOrganizeCategories())} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  onOrganizeCategories?.();
                  handleCloseContextMenu();
                }}
                disabled={!canOrganizeCategories()}
              >
                <ListItemIcon>
                  <AutoFixHighDirectionIcon direction="north" variant="menu" />
                </ListItemIcon>
                <ListItemText>Structure categories (AI)</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          {onCollectBranchContent && (
            <Tooltip title={getMenuTooltipTextForState('collapse-subtree-to-content-category', canCollectBranchContent())} placement="right">
              <span>
                <MenuItem
                  onClick={() => {
                    onCollectBranchContent();
                    handleCloseContextMenu();
                  }}
                  disabled={!canCollectBranchContent()}
                >
                  <ListItemIcon>
                    <ViewListOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Collect branch content</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
          <Tooltip title={getMenuTooltipTextForState('merge-categories', !!(selectedCategories.length > 0 && canMergeCategories))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  onMergeCategories?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canMergeCategories}
              >
                <ListItemIcon>
                  <CallMergeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Merge categories</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip
            title={getMenuTooltipTextForState('rename-category', !!canRenamePaneCategory?.())}
            placement="right"
          >
            <span>
              <MenuItem
                onClick={() => {
                  onRenameCategory?.();
                  handleCloseContextMenu();
                }}
                disabled={!canRenamePaneCategory?.()}
              >
                <ListItemIcon>
                  <DriveFileRenameOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Rename Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip
            title={getMenuTooltipTextForState('delete-category', !!canDeletePaneCategories?.())}
            placement="right"
          >
            <span>
              <MenuItem
                onClick={() => {
                  onDeleteSelected?.();
                  handleCloseContextMenu();
                }}
                disabled={!canDeletePaneCategories?.()}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('move-category-up', !!(selectedCategories.length > 0 && canMoveSelected('up')))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onMoveSelected?.('up');
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canMoveSelected('up')}
              >
                <ListItemIcon>
                  <ArrowUpwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Up</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('move-category-down', !!(selectedCategories.length > 0 && canMoveSelected('down')))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onMoveSelected?.('down');
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canMoveSelected('down')}
              >
                <ListItemIcon>
                  <ArrowDownwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Down</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('cut-category', !!(selectedCategories.length > 0 && canCutSelected))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  onCutSelected?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canCutSelected}
              >
                <ListItemIcon>
                  <ContentCutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cut</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('copy-category', !!(selectedCategories.length > 0 && canCopySelected))} placement="right">
            <span>
              <MenuItem 
                onClick={() => {
                  onCopySelected?.();
                  handleCloseContextMenu();
                }}
                disabled={selectedCategories.length === 0 || !canCopySelected}
              >
                <ListItemIcon>
                  <ContentCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
        </TableContainer>
        </>,
      )}
    </ContentContainer>
  );
});


export default CategoryContent; 